/**
 * 为图层管理中的组合生成静态预览，依赖画布运行时对象和操作历史事件
 * 含 DOM 图层或隐藏后缺少资源的组合保留文件夹图标，不调用导出或 DOM 截图链路。
 */
import { memo, useEffect, useRef, useState } from 'react';
import IconFont from '@meitu/xdesign-icon-font';
import { getFramework, type EdgelessObject } from '@/framework';
import { getRotatePosition } from '@/utils/collision-kit';
import type { ILayerProps } from '@/version-types';

/** 40 CSS 像素缩略图按两倍像素绘制 */
const PREVIEW_SIZE = 80;
/** 合并操作提交及随后到达的资源刷新 */
const REFRESH_DELAY = 150;

type Props = { identity: string; layerId: string };

/**
 * 在图层管理组合行挂载静态快照，依赖操作历史及子图层资源就绪状态
 *
 * @param props - 编辑器身份和组合图层 ID
 * @returns 组合内容画布；包含 DOM 图层或无法生成内容时返回文件夹图标
 */
export const GroupThumbnail = memo(function GroupThumbnail({ identity, layerId }: Props) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [showFolder, setShowFolder] = useState(false);

    useEffect(() => {
        const { flatLayer, edgelessCanvas, history, frame, events } = getFramework(identity).service;
        let timer: ReturnType<typeof setTimeout> | undefined;
        let resources: unknown[] = [];

        const collect = () => {
            const objects: EdgelessObject[] = [];
            const signatures: unknown[] = [];
            let containsDom = false;
            let missingHiddenResource = false;
            const root = flatLayer.getLayer(layerId);
            const rootHidden = !!root && flatLayer.checkIsNeedHidden(root);
            const visit = (layer: ILayerProps, hidden: boolean) => {
                const object = edgelessCanvas.getByLayerId(layer.id);
                // 即使 DOM 子图层隐藏，也保留整个组合的文件夹语义。
                if (object?.domRender) containsDom = true;
                if (layer.id !== layerId) {
                    hidden = hidden || layer.hidden;
                    // 隐藏图层可能尚未创建运行时对象或加载 drawable，避免绘出空白占位。
                    if ((rootHidden || hidden) && !layer.children?.length && !object?.noRender && !object?.resource) {
                        missingHiddenResource = true;
                    }
                    signatures.push(object, object?.requiresContinuousRender ? !!object.resource : object?.resource);
                    if (
                        object &&
                        !object.noRender &&
                        !object.domRender &&
                        !hidden &&
                        object.width > 0 &&
                        object.height > 0
                    ) {
                        objects.push(object);
                    }
                }
                layer.children?.forEach((child) => visit(child, hidden));
            };
            if (root) visit(root, false);
            return { objects, signatures, useFolder: containsDom || missingHiddenResource || !objects.length };
        };

        const draw = () => {
            timer = undefined;
            const { objects, signatures, useFolder } = collect();
            resources = signatures;
            setShowFolder(useFolder);
            const canvas = canvasRef.current;
            if (!canvas) return;
            canvas.width = PREVIEW_SIZE;
            canvas.height = PREVIEW_SIZE;
            const ctx = canvas.getContext('2d');
            if (!ctx || useFolder) return;

            const sources = objects.map((object) => ({ object, box: object.getBaseInfo() }));
            const points = sources.flatMap(({ object, box }) =>
                getRotatePosition({ ...box, rotate: object.angle }, { x: box.left, y: box.top }),
            );
            const left = Math.min(...points.map((point) => point.x));
            const top = Math.min(...points.map((point) => point.y));
            const width = Math.max(...points.map((point) => point.x)) - left;
            const height = Math.max(...points.map((point) => point.y)) - top;
            const scale = PREVIEW_SIZE / Math.max(width, height, 1);
            ctx.translate((PREVIEW_SIZE - width * scale) / 2, (PREVIEW_SIZE - height * scale) / 2);
            ctx.scale(scale, scale);
            for (const { object, box } of sources) {
                ctx.save();
                ctx.translate(box.left - left, box.top - top);
                ctx.rotate((object.angle * Math.PI) / 180);
                ctx.globalAlpha = object.opacity;
                try {
                    // 媒体复用封面绘制，既不推进播放，也不创建逐帧订阅。
                    object.renderPreview(ctx);
                } finally {
                    ctx.restore();
                }
            }
        };

        const schedule = () => {
            if (timer) clearTimeout(timer);
            timer = setTimeout(draw, REFRESH_DELAY);
        };
        const historyListener = history.onChange(schedule);
        const renderListener = frame.onRenderComplete(({ frameId }) => {
            if (frameId !== frame.getLayerFrameId(layerId)) return;
            const { signatures } = collect();
            // 原地更新的动画资源不使快照失效；静态资源异步替换后补画一次。
            if (
                signatures.length === resources.length &&
                signatures.every((value, index) => value === resources[index])
            )
                return;
            resources = signatures;
            schedule();
        });
        draw();
        return () => {
            if (timer) clearTimeout(timer);
            events.off(historyListener);
            events.off(renderListener);
        };
    }, [identity, layerId]);

    return (
        <>
            <canvas
                ref={canvasRef}
                aria-hidden
                className="resource-thumbnail"
                style={{ width: '100%', height: '100%', display: showFolder ? 'none' : 'block' }}
            />
            {showFolder ? <IconFont name="Folder" fontSize="1em" /> : null}
        </>
    );
});
